import { useMemo, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { ErrorBanner } from '@/components/ErrorBanner';
import { formatNumber } from '@/i18n/format';
import type { LabelLayout, LabelScope, LocationLabelsResult, WarehouseStructure } from '@/api/types';
import { describeSetupError } from './setup';
import { downloadLocationLabels } from './labels';

/** Mirrors the server cap (MAX_LABELS_PER_REQUEST). */
const MAX_LABELS = 500;

type Pickable = { id: string; code: string };
type Group = { key: string; title: string; items: readonly Pickable[] };

/**
 * "Download labels": whole warehouse, one zone or hand-picked cells, A4 8-up or a 100x60 mm label. The server
 * renders the PDF (display code + Code 128 barcode); this component only chooses what to print and downloads it.
 * Every control is a >= 44px touch target and a native radio/checkbox, so it works the same on a phone.
 */
export function LabelsPanel({ data }: { data: WarehouseStructure }): JSX.Element {
  const { t, locale } = useLanguage();
  const [layout, setLayout] = useState<LabelLayout>('A4_8UP');
  const [scope, setScope] = useState<LabelScope>('WAREHOUSE');
  const [zoneId, setZoneId] = useState<string>(data.zones[0]?.id ?? '');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<LocationLabelsResult | null>(null);

  const groups = useMemo<Group[]>(() => {
    const out: Group[] = [];
    const sys = data.systemLocations.flatMap((l) => (l.id && l.code ? [{ id: l.id, code: l.code }] : []));
    if (sys.length > 0) out.push({ key: 'system', title: t('labels.systemGroup'), items: sys });
    for (const z of data.zones) {
      const cells = data.cells.filter((c) => c.zone_id === z.id);
      if (cells.length > 0) out.push({ key: z.id, title: z.name, items: cells });
    }
    if (data.otherLocations.length > 0) out.push({ key: 'other', title: t('labels.otherGroup'), items: data.otherLocations });
    return out;
  }, [data, t]);

  const wholeCount = groups.reduce((s, g) => s + g.items.length, 0);
  const zoneCount = data.cells.filter((c) => c.zone_id === zoneId).length;
  const count = scope === 'WAREHOUSE' ? wholeCount : scope === 'ZONE' ? zoneCount : picked.size;
  const tooMany = count > MAX_LABELS;
  const canDownload = !busy && count > 0 && !tooMany && (scope !== 'ZONE' || zoneId !== '');

  const toggle = (id: string, on: boolean): void =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const toggleGroup = (g: Group, on: boolean): void =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const i of g.items) {
        if (on) next.add(i.id);
        else next.delete(i.id);
      }
      return next;
    });

  const download = (): void => {
    setBusy(true);
    setError(null);
    setDone(null);
    const input =
      scope === 'WAREHOUSE'
        ? { scope, layout }
        : scope === 'ZONE'
          ? { scope, zoneId, layout }
          : { scope, locationIds: [...picked], layout };
    downloadLocationLabels(data.warehouse.id, input)
      .then(setDone, setError)
      .finally(() => setBusy(false));
  };
  const errText = error ? describeSetupError(t, error) : null;
  const n = (v: number): string => formatNumber(v, locale);

  const layoutOption = (value: LabelLayout, label: Parameters<typeof t>[0], hint: Parameters<typeof t>[0]) => (
    <label className={`purpose-option${layout === value ? ' selected' : ''}`}>
      <input type="radio" name="label-layout" value={value} checked={layout === value} disabled={busy} onChange={() => setLayout(value)} />
      <span>
        <strong>{t(label)}</strong>
        <span className="field-hint">{t(hint)}</span>
      </span>
    </label>
  );
  const scopeOption = (value: LabelScope, text: string) => (
    <label className={`purpose-option${scope === value ? ' selected' : ''}`}>
      <input type="radio" name="label-scope" value={value} checked={scope === value} disabled={busy} onChange={() => setScope(value)} />
      <span><strong>{text}</strong></span>
    </label>
  );

  return (
    <section className="card labels-panel" aria-labelledby="labels-title">
      <h2 id="labels-title" className="card-title">{t('labels.title')}</h2>
      <p className="field-hint">{t('labels.intro')}</p>

      <fieldset className="purpose-choice">
        <legend>{t('labels.layoutLegend')}</legend>
        {layoutOption('A4_8UP', 'labels.layout.A4_8UP', 'labels.layoutHint.A4_8UP')}
        {layoutOption('LABEL_100x60', 'labels.layout.LABEL_100x60', 'labels.layoutHint.LABEL_100x60')}
      </fieldset>

      <fieldset className="purpose-choice">
        <legend>{t('labels.scopeLegend')}</legend>
        {scopeOption('WAREHOUSE', t('labels.scope.WAREHOUSE', { n: n(wholeCount) }))}
        {scopeOption('ZONE', t('labels.scope.ZONE'))}
        {scope === 'ZONE' && (
          <div className="field-row">
            <label htmlFor="labels-zone">{t('labels.zonePick')}</label>
            <select id="labels-zone" value={zoneId} disabled={busy} onChange={(e) => setZoneId(e.target.value)}>
              {data.zones.map((z) => (
                <option key={z.id} value={z.id}>{`${z.name} (${n(data.cells.filter((c) => c.zone_id === z.id).length)})`}</option>
              ))}
            </select>
          </div>
        )}
        {scopeOption('SELECTED', t('labels.scope.SELECTED'))}
      </fieldset>

      {scope === 'SELECTED' && (
        <div className="label-picker">
          {groups.map((g) => {
            const allOn = g.items.every((i) => picked.has(i.id));
            return (
              <fieldset key={g.key} className="label-group">
                <legend>{g.title}</legend>
                <label className="check-row">
                  <input type="checkbox" checked={allOn} disabled={busy} onChange={(e) => toggleGroup(g, e.target.checked)} />
                  <span>{t('labels.selectAllIn', { zone: g.title })}</span>
                </label>
                <ul className="cell-grid">
                  {g.items.map((i) => (
                    <li key={i.id}>
                      <label className="check-row label-cell">
                        <input type="checkbox" checked={picked.has(i.id)} disabled={busy} onChange={(e) => toggle(i.id, e.target.checked)} />
                        <span className="mono">{i.code}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </fieldset>
            );
          })}
          {groups.length === 0 && <p className="sub">{t('labels.nothing')}</p>}
        </div>
      )}

      <p className="field-hint" aria-live="polite">{t('labels.count', { n: n(count), max: n(MAX_LABELS) })}</p>
      {tooMany && <p className="preview-line warn" role="alert">{t('labels.tooMany', { max: n(MAX_LABELS) })}</p>}
      {done && (
        <p className="ok-note" role="status">
          {t('labels.done', { n: n(done.labelCount), pages: n(done.pageCount) })}
          {done.archivedCount > 0 ? ` ${t('labels.doneArchived', { n: n(done.archivedCount) })}` : ''}
        </p>
      )}
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="button" className="primary" disabled={!canDownload} onClick={download}>
          {busy ? t('labels.preparing') : t('labels.download')}
        </button>
      </div>
    </section>
  );
}
